import { apmBase, init as initApm } from "@elastic/apm-rum";

/**
 * Initializes Elastic APM RUM.
 * Must be called as early as possible — before any async work — so the page-load
 * transaction captures accurate timing.
 *
 * Also registers a capture-phase unhandledrejection normalizer that intercepts raw
 * browser Event objects (CloseEvent, ProgressEvent, etc.) before APM's bubble-phase
 * handler serializes them as the useless {"isTrusted":true} string.
 */
export function setupApmInit(): void {
  if (import.meta.env.APP_APM_ENABLED !== "true") return;

  // Register in the CAPTURE phase so this fires before APM's bubble-phase listener.
  window.addEventListener(
    "unhandledrejection",
    (event) => {
      const reason = event.reason;
      if (
        reason != null &&
        typeof reason === "object" &&
        !(reason instanceof Error) &&
        (reason as Event).isTrusted === true
      ) {
        event.stopImmediatePropagation();
        event.preventDefault();
        const type = (reason as Event).type ?? "unknown";
        apmBase.captureError(new Error(`Unhandled rejection: ${type} event (network/WebSocket failure)`));
      }
    },
    true, // capture phase — fires before APM's bubble-phase handler
  );

  initApm({
    serviceName: import.meta.env.APP_APM_SERVICE_NAME,
    environment: import.meta.env.APP_APM_ENVIRONMENT,
    serverUrl: import.meta.env.APP_APM_SERVER_URL,
    // Same-origin /graphql is proxied by IIS ARR to the backend, so include both
    // location.origin (for same-origin fetch) and APP_BACKEND_URL (for dev direct calls).
    distributedTracingOrigins: [
      globalThis.location.origin,
      import.meta.env.APP_BACKEND_URL as string,
    ].filter(Boolean),
  });
}
